<script setup lang="ts">
defineProps<{
  title?: string
}>()

const { $SplitText, $gsap, $CustomEase } = useNuxtApp()

const $text = useTemplateRef('$text')

const isSplit = ref(false)
const isSeen = ref(false)

let scrollTriggers: gsap.core.ScrollTrigger[] = []
let splitTextInstance: SplitText | null = null
let observer: IntersectionObserver | null = null

function initScrollTriggers(splitText: SplitText) {
  scrollTriggers.forEach((trigger) => trigger.kill())
  scrollTriggers = []

  splitText.lines.forEach((line, idx, lines) => {
    const median = (lines.length - 1) / 2
    const distanceFromMedian = Math.abs(idx - median)
    const baseTranslate = 1
    const translate = (baseTranslate + distanceFromMedian + idx) * 0.25

    const tween = $gsap.fromTo(
      line,
      { x: `${translate * -1}rem` },
      {
        x: `${translate}rem`,
        ease: $CustomEase.create('custom', '0,.4,1,.6'),
        scrollTrigger: {
          trigger: $text.value,
          scrub: true,
          start: `top bottom`,
          end: `bottom top`,
        },
      },
    )

    scrollTriggers.push(tween.scrollTrigger!)
  })

  isSplit.value = true
}

onMounted(() => {
  if (import.meta.server || !$text.value) return

  splitTextInstance = $SplitText.create($text.value, {
    type: 'lines',
    linesClass: 'line',
    mask: 'lines',
    autoSplit: true,
    onSplit: initScrollTriggers,
  })

  const observerCallback: IntersectionObserverCallback = ([entry], obs) => {
    if (entry?.isIntersecting) {
      isSeen.value = true
      obs.disconnect()
    }
  }

  observer = new IntersectionObserver(observerCallback, { rootMargin: '-100px' })
  observer.observe($text.value)
})

onBeforeUnmount(() => {
  // Kill all ScrollTriggers
  scrollTriggers.forEach((trigger) => trigger.kill())
  scrollTriggers = []

  // Revert SplitText
  splitTextInstance?.revert?.()
  splitTextInstance = null

  // Disconnect observer
  observer?.disconnect()
  observer = null
})
</script>

<template>
  <h3
    v-if="title"
    ref="$text"
    class="text-reveal-v2"
    :class="{ 'opacity-0': !isSplit, 'is-seen': isSeen }"
  >
    {{ title }}
  </h3>
</template>

<style lang="postcss">
.text-reveal-v2 {
  --duration: 600ms;
  --stagger: 200ms;

  .line-mask {
    overflow: visible !important;
    will-change: transform;
    transition: transform calc(var(--duration) * 2) var(--ease-out-quint);
    transform: translateX(-4rem);

    &:nth-of-type(2) {
      .line {
        transition-delay: calc(var(--stagger) * 1);
      }
    }

    &:nth-of-type(3) {
      .line {
        transition-delay: calc(var(--stagger) * 2);
      }
    }

    &:nth-of-type(4) {
      .line {
        transition-delay: calc(var(--stagger) * 3);
      }
    }

    &:nth-of-type(5) {
      .line {
        transition-delay: calc(var(--stagger) * 4);
      }
    }
  }

  .line {
    transition: clip-path var(--duration) var(--ease-in-out-quint);

    clip-path: polygon(-100% 0%, 0% 0%, -05% 100%, -100% 100%);
    -webkit-clip-path: polygon(-100% 0%, 0% 0%, -05% 100%, -100% 100%);

    display: inline-block !important;
    will-change: transform;
  }

  &.is-seen {
    .line-mask {
      transform: translateX(0);
    }

    .line {
      clip-path: polygon(-100% 0%, 105% 0%, 100% 100%, -100% 100%);
      -webkit-clip-path: polygon(-100% 0%, 105% 0%, 100% 100%, -100% 100%);
    }
  }
}
</style>
